import { Controller } from 'stimulus'
import './address_field_controller.css'
import {
  loader,
  googleMapsAvailable,
} from '~/common/helpers/google_maps_loader'

// TODO: Combine this with the currency change listener for non-selectize fields in forms.coffee and eliminate site/pages/cart/country.js
export default class extends Controller {
  static values = {
    intialCountryCode: String,
    ignoreRegionCallback: Boolean,
    noRegion: Boolean,
  }

  static targets = [
    'addressField',
    'address2Field',
    'countryField',
    'regionWrapper',
    'regionField',
    'regionLabel',
    'postalCodeField',
    'postalCodeLabel',
    'cityField',
  ]

  connect() {
    this.googleAutocompleteInitialize()
  }

  googleAutocompleteInitialize() {
    if (!googleMapsAvailable || !this.hasAddressFieldTarget) {
      return
    }

    loader.importLibrary('places').then(({ Autocomplete }) => {
      this.autocomplete = new Autocomplete(this.addressFieldTarget, {
        types: ['address'],
      })
      this.setCountryForAutocomplete()
      this.autocompleteListener = this.autocomplete.addListener(
        'place_changed',
        this.googlePlaceToFormFields.bind(this)
      )
    })

    this.addressFieldTarget.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.stopPropagation()
        e.preventDefault()
        return false
      }
    })
  }

  googlePlaceToFormFields() {
    const place = this.autocomplete.getPlace()

    if (!place.address_components) {
      return
    }

    let address = ''
    let postcode = ''
    let region = ''

    // fill-in the corresponding this.addressFieldTarget on the form.
    // place.address_components are google.maps.GeocoderAddressComponent objects
    // which are documented at http://goo.gle/3l5i5Mr
    for (const component of place.address_components) {
      const componentType = component.types[0]

      switch (componentType) {
        case 'street_number': {
          address = `${component.long_name} ${address}`
          break
        }

        case 'route': {
          // in some countries the street number goes at the end of the address.
          // the "address" variable would already contain the street number reached this point, if any. e.g. "25"
          address = this.usesStreetNumberAtEnd(this.countryFieldTarget?.value)
            ? component.short_name + ' ' + address
            : address + component.short_name
          break
        }

        case 'postal_code': {
          postcode = `${component.long_name}${postcode}`
          break
        }

        case 'postal_code_suffix': {
          postcode = `${postcode}-${component.long_name}`
          break
        }

        case 'administrative_area_level_1': {
          region = component.short_name
          break
        }
      }
    }

    let regionSelectedViaDropdown = false
    if (region && this.hasRegionFieldTarget) {
      // When region uses dropdown_select, ask its controller to select by value (the geocoder's
      // short-name abbreviation), falling back to text matching in that controller for countries
      // where no abbreviation is returned (e.g. Norway). For native selects, keep direct value assignment.
      const control = this.hasRegionWrapperTarget
        ? this.regionWrapperTarget.querySelector('.js-dropdown-select')
        : null

      if (control) {
        control.dispatchEvent(
          new CustomEvent('address-field:select-value', {
            detail: { value: region },
          })
        )
        regionSelectedViaDropdown = true
      } else if (
        this.regionFieldTarget.querySelector(`option[value="${region}"]`)
      ) {
        this.regionFieldTarget.value = region
      } else {
        // Native <select>: the geocoder returns no abbreviation for some countries (e.g. Norway),
        // so fall back to matching the option by its visible text.
        const regionNameMatch = Array.from(this.regionFieldTarget.options).find(
          (o) => o.text == region
        )
        if (regionNameMatch)
          this.regionFieldTarget.value = regionNameMatch.value
      }
    }

    if (this.hasCityFieldTarget) {
      this.cityFieldTarget.value = this.deriveCityFieldFromComponents(
        place.address_components
      )
    }

    if (this.hasAddress2FieldTarget) {
      this.address2FieldTarget.value = '' // check if we can get something from goolge for this field
    }
    if (this.hasPostalCodeFieldTarget) {
      this.postalCodeFieldTarget.value = postcode
    }

    this.addressFieldTarget.value = address.trim()

    this.addressAutocompleted()
    // if region and/or postal code was present in google's response, we better trigger change event
    if (region && !regionSelectedViaDropdown) this.changeRegion()
    if (postcode) this.changePostalCode()
  }

  findComponent(components, type) {
    for (const component of components) {
      if (component.types.includes(type)) {
        return component
      }
    }
  }

  // NYC and some other places are special on Google, and do not respond with locality
  // @see: https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform
  deriveCityFieldFromComponents(components) {
    const postalTown = this.findComponent(components, 'postal_town')?.long_name
    const sublocalityLevelOne = this.findComponent(
      components,
      'sublocality_level_1'
    )?.long_name
    const locality = this.findComponent(components, 'locality')?.long_name

    return locality || postalTown || sublocalityLevelOne
  }

  addressAutocompleted() {
    this.addressChanged = true
    this.element.classList.add('address-selected')
  }

  setCountryForAutocomplete() {
    if (!this.autocomplete || !this.countryFieldTarget.value) return

    this.autocomplete.setComponentRestrictions({
      country: [this.countryFieldTarget.value],
    })
  }

  async changeCountry(event) {
    this.dispatch('country-changed')

    this.setCountryForAutocomplete()

    // countryFieldTarget is the dropdown_select's hidden input (which is also event.target).
    const countryCode = this.hasCountryFieldTarget
      ? this.countryFieldTarget.value
      : event.target.value
    // Don't request if the value hasn't changed
    if (countryCode == this.initialCountryCodeValue) return

    // Don't request if we want to ignore region callbacks
    if (this.ignoreRegionCallbackValue) return

    try {
      await this.updateCountryFields(countryCode)
    } catch (e) {
      alert('Something went wrong. Please try again...')
      throw e
    }
  }

  // Update the address controls without notifying the surrounding form. Wallet
  // prefill uses this while its payment sheet must stay mounted.
  async updateCountryFields(countryCode, { signal } = {}) {
    const regionField = this.hasRegionFieldTarget
      ? this.regionFieldTarget
      : null
    const regionWasDisabled = regionField?.disabled
    // Let's disable the region field, while we do the request...
    if (regionField) regionField.disabled = true

    const url = new URL('/form_helper/subregions', window.location.origin)
    url.searchParams.set('country_code', countryCode)
    // Ask the server for the new country's subregions. For a native <select> region we rebuild its
    // <option>s from that data; for a dropdown_select (whose searchable options can't be rebuilt
    // client-side) the server also returns a freshly rendered control to swap in — hence field_name.
    // The previously selected region is intentionally not preserved — it's invalid under the new
    // country. Fall back to the detached wrapper's field name so we can still rebuild when coming
    // back from a no-region country (which removes the live region target from the DOM).
    const regionFieldName = this.regionFieldName()
    if (regionFieldName) url.searchParams.set('field_name', regionFieldName)

    // Preserve the form association (set when the address field lives outside its <form>, e.g. the
    // page builder) so the swapped-in region control's hidden input keeps submitting with the form.
    const regionForm = this.regionFieldForm()
    if (regionForm) url.searchParams.set('field_form', regionForm)

    try {
      this.element.dataset.countryChangeLoading = true
      const response = await fetch(url, { signal })
      if (!response.ok) throw new Error('Could not load address regions')
      const json = await response.json()
      signal?.throwIfAborted()

      // Update labels
      if (this.hasPostalCodeLabelTarget)
        this.postalCodeLabelTarget.innerText = json.zip_label
      else if (this.hasPostalCodeFieldTarget)
        this.postalCodeFieldTarget.placeholder = json.zip_label

      if (json.subregions.length == 0) {
        this.noRegionValue = true
      } else {
        this.attachRegionWrapperTarget()
        this.noRegionValue = false
        // Update the label after re-attaching, so the target exists when coming back from a
        // no-region country (the region wrapper, and its label, were removed from the DOM).
        if (this.hasRegionLabelTarget)
          this.regionLabelTarget.innerText = json.label
        this.replaceRegionControl(json)
      }
    } catch (error) {
      if (regionField) regionField.disabled = regionWasDisabled
      throw error
    } finally {
      delete this.element.dataset.countryChangeLoading
    }
  }

  // The region field's name, read from the live target or, when the region wrapper is currently
  // detached (a no-region country was selected), from the detached wrapper we kept a reference to.
  regionFieldName() {
    if (this.hasRegionFieldTarget) return this.regionFieldTarget.name
    return this.detachedRegionWrapperTarget?.querySelector(
      'input[type="hidden"]'
    )?.name
  }

  // The region field's `form` attribute, read from the live target or the detached wrapper, so the
  // server-rendered replacement keeps the same form association.
  regionFieldForm() {
    if (this.hasRegionFieldTarget)
      return this.regionFieldTarget.getAttribute('form')
    return this.detachedRegionWrapperTarget
      ?.querySelector('input[type="hidden"]')
      ?.getAttribute('form')
  }

  // Rebuild the region control for the newly selected country. Two rendering flavors exist:
  //   • dropdown_select (Boxy/Tailwind forms): swap in the server-rendered `.js-dropdown-select`,
  //     whose searchable options can't be rebuilt client-side. Stimulus re-binds targets on the
  //     replacement automatically.
  //   • native <select> (cart checkout & custom-field address fields): rebuild its <option>s in
  //     place from the subregions data — keeping the same element preserves its name, Stimulus
  //     targets and native `required` validation.
  // Either way the previously selected region is dropped — it's invalid under the new country.
  replaceRegionControl(json) {
    if (!this.hasRegionWrapperTarget) {
      if (this.hasRegionFieldTarget) this.regionFieldTarget.disabled = false
      return
    }

    const dropdown = this.regionWrapperTarget.querySelector(
      '.js-dropdown-select'
    )
    if (dropdown) {
      if (json.field_html) {
        const tmp = document.createElement('div')
        tmp.innerHTML = json.field_html.trim() // server-rendered markup
        dropdown.replaceWith(tmp.firstElementChild)
      } else if (this.hasRegionFieldTarget) {
        this.regionFieldTarget.disabled = false
      }
      return
    }

    const select = this.regionWrapperTarget.querySelector('select')
    if (select) this.rebuildNativeRegionOptions(select, json)
    else if (this.hasRegionFieldTarget) this.regionFieldTarget.disabled = false
  }

  // Repopulate a native region <select> with the new country's subregions. A leading blank option
  // is kept (value="") so the field resets to unselected and `required` validation still forces an
  // explicit choice.
  rebuildNativeRegionOptions(select, json) {
    const blank = document.createElement('option')
    blank.value = ''
    blank.textContent = json.blank_label || ''

    const options = [blank]
    for (const subregion of json.subregions) {
      const option = document.createElement('option')
      option.value = subregion.value
      option.textContent = subregion.text
      options.push(option)
    }

    select.replaceChildren(...options)
    select.value = ''
    select.disabled = false
  }

  changeRegion() {
    this.dispatch('region-changed')
  }

  changePostalCode() {
    this.dispatch('postal-code-changed')
  }

  // We do this dance because we want to remove the field from the DOM so it's not considered by our CSS selectors
  // that rely on first/last-child for border/focus styling.
  noRegionValueChanged() {
    if (this.noRegionValue && this.hasRegionWrapperTarget) {
      this.detachedRegionWrapperTarget = this.regionWrapperTarget
      this.regionWrapperTarget.remove()
    }
  }
  attachRegionWrapperTarget() {
    if (!this.detachedRegionWrapperTarget) return
    this.element.appendChild(this.detachedRegionWrapperTarget)
    this.detachedRegionWrapperTarget = null
  }

  toggleAddress2() {
    this.element.classList.toggle('address2-visible')
  }

  // Some countries addresses put the street number at the end, not the begginning
  usesStreetNumberAtEnd(countryCode) {
    return (
      [
        'AT', // Austria
        'CH', // Switzerland
        'BE', // Belgium
        'PT', // Portugal
        'GR', // Greece
        'DK', // Denmark
        'NO', // Norway
        'FI', // Finland
        'LU', // Luxembourg
        'TR', // Turkey
        'CZ', // Czech Republic
        'HU', // Hungary
        'PL', // Poland
        'RO', // Romania
        'SK', // Slovakia
        'BG', // Bulgaria
        'SI', // Slovenia
        'HR', // Croatia
        'BA', // Bosnia and Herzegovina
        'RS', // Serbia
        'FR', // France
        'DE', // Germany
        'ES', // Spain
        'IT', // Italy
        'SE', // Sweden
      ].indexOf(countryCode) > -1
    )
  }
}
